import React from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { FaGithub, FaLinkedin, FaEnvelope, FaMapMarkerAlt, FaYoutube, FaApple, FaGooglePlay } from 'react-icons/fa';
import { contactInfo } from '../data';
import { productCatalog } from '../data/products';
import profilePhoto from '../assets/pradap-photo.jpg';
import { useTheme } from '../contexts/ThemeContext';
import MagneticButton from './MagneticButton';

/** App icons that float / drift around the hero visual */
const FloatingProductIcons: React.FC = () => {
  const navigate = useNavigate();
  const { themeClasses, accent } = useTheme();

  const apps = productCatalog
    .filter((p) => p.status === 'Published' && Boolean(p.iosUrl || p.androidUrl) && p.iconUrl)
    .slice(0, 8);

  // Staggered orbits / floats so icons feel alive without looking chaotic
  const layouts = [
    { top: '6%', left: '12%', size: 'w-16 h-16 sm:w-20 sm:h-20', duration: 5.5, x: [0, 14, -8, 0], y: [0, -18, 10, 0], rotate: [0, 6, -4, 0] },
    { top: '2%', left: '48%', size: 'w-14 h-14 sm:w-16 sm:h-16', duration: 6.2, x: [0, -16, 10, 0], y: [0, 14, -12, 0], rotate: [0, -8, 5, 0] },
    { top: '18%', left: '72%', size: 'w-16 h-16 sm:w-20 sm:h-20', duration: 4.8, x: [0, 10, -14, 0], y: [0, -12, 16, 0], rotate: [0, 5, -6, 0] },
    { top: '38%', left: '8%', size: 'w-14 h-14 sm:w-16 sm:h-16', duration: 7.1, x: [0, 18, -6, 0], y: [0, 10, -16, 0], rotate: [0, -5, 8, 0] },
    { top: '42%', left: '42%', size: 'w-20 h-20 sm:w-24 sm:h-24', duration: 5.2, x: [0, -12, 14, 0], y: [0, -16, 8, 0], rotate: [0, 4, -5, 0] },
    { top: '36%', left: '74%', size: 'w-14 h-14 sm:w-16 sm:h-16', duration: 6.6, x: [0, 8, -16, 0], y: [0, 18, -10, 0], rotate: [0, -7, 4, 0] },
    { top: '68%', left: '22%', size: 'w-16 h-16 sm:w-20 sm:h-20', duration: 5.8, x: [0, -14, 12, 0], y: [0, -10, 14, 0], rotate: [0, 6, -4, 0] },
    { top: '70%', left: '58%', size: 'w-14 h-14 sm:w-16 sm:h-16', duration: 4.6, x: [0, 16, -10, 0], y: [0, 12, -14, 0], rotate: [0, -5, 7, 0] }
  ];

  return (
    <div
      className="relative w-full aspect-square max-w-[22rem] sm:max-w-[24rem] md:max-w-[26rem]"
      aria-label="Published iOS and Android apps"
    >
      {/* Soft atmosphere */}
      <div
        className={`absolute inset-[8%] rounded-full opacity-40 blur-3xl bg-gradient-to-br ${themeClasses.gradient}`}
      />
      <div className="absolute inset-[18%] rounded-full border border-white/5" />
      <div className="absolute inset-[32%] rounded-full border border-white/5" />

      {apps.map((app, i) => {
        const layout = layouts[i % layouts.length];
        const storeUrl = app.iosUrl || app.androidUrl || '/products';
        const isExternal = Boolean(app.iosUrl || app.androidUrl);

        return (
          <motion.a
            key={app.id}
            href={isExternal ? storeUrl! : undefined}
            onClick={(e) => {
              if (!isExternal) {
                e.preventDefault();
                navigate('/products');
              }
            }}
            target={isExternal ? '_blank' : undefined}
            rel={isExternal ? 'noopener noreferrer' : undefined}
            title={app.name}
            aria-label={`${app.name} — ${app.platform}`}
            className={`absolute ${layout.size} rounded-[22%] overflow-hidden shadow-lg shadow-black/40 border border-white/15 bg-black/40 backdrop-blur-sm cursor-pointer touch-manipulation`}
            style={{ top: layout.top, left: layout.left }}
            initial={{ opacity: 0, scale: 0.6 }}
            animate={{
              opacity: 1,
              scale: 1,
              x: layout.x,
              y: layout.y,
              rotate: layout.rotate
            }}
            transition={{
              opacity: { delay: 0.2 + i * 0.08, duration: 0.4 },
              scale: { delay: 0.2 + i * 0.08, duration: 0.4 },
              x: { duration: layout.duration, repeat: Infinity, ease: 'easeInOut', delay: i * 0.15 },
              y: { duration: layout.duration, repeat: Infinity, ease: 'easeInOut', delay: i * 0.15 },
              rotate: { duration: layout.duration, repeat: Infinity, ease: 'easeInOut', delay: i * 0.15 }
            }}
            whileHover={{ scale: 1.12, zIndex: 20 }}
            whileTap={{ scale: 0.96 }}
          >
            <img
              src={app.iconUrl}
              alt=""
              className="h-full w-full object-cover"
              loading="lazy"
              decoding="async"
              draggable={false}
            />
            <span className="pointer-events-none absolute bottom-1 right-1 flex gap-0.5">
              {app.iosUrl && (
                <span className="rounded-full bg-black/55 p-0.5 text-white">
                  <FaApple className="h-2.5 w-2.5" />
                </span>
              )}
              {app.androidUrl && (
                <span className="rounded-full bg-black/55 p-0.5 text-emerald-300">
                  <FaGooglePlay className="h-2.5 w-2.5" />
                </span>
              )}
            </span>
          </motion.a>
        );
      })}

      <motion.button
        type="button"
        onClick={() => navigate('/products')}
        className={`absolute bottom-2 left-1/2 z-10 -translate-x-1/2 rounded-full border px-3 py-1.5 text-xs font-semibold backdrop-blur-md ${accent.buttonOutline} ${themeClasses.bg}/70`}
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.97 }}
      >
        View all products
      </motion.button>
    </div>
  );
};

const Hero: React.FC = () => {
  const { theme, themeClasses } = useTheme();
  const navigate = useNavigate();
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        delayChildren: 0.3,
        staggerChildren: 0.2
      }
    }
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1
    }
  };

  const goToContact = () => navigate('/contact');
  const goToProducts = () => navigate('/products');


  const getRainColor = () => {
    switch(theme) {
      case 'slate': return 'bg-teal-400';
      case 'slate': return 'bg-teal-400';
      case 'midnight': return 'bg-sky-400';
      case 'studio': return 'bg-teal-700';
      case 'slate': return 'bg-teal-400';
      case 'slate': return 'bg-sky-400';
      default: return 'bg-teal-400';
    }
  };

  return (
    <section id="home" className={`min-h-screen flex items-center justify-center pt-20 sm:pt-24 ${themeClasses.bg} relative overflow-hidden px-4 sm:px-6`}>
      {/* Matrix Rain Background Effect */}
      <div className="absolute inset-0 opacity-20">
        <div className={`matrix-rain absolute left-1/4 top-0 w-px h-full ${getRainColor()} animate-matrix-rain`}></div>
        <div className={`matrix-rain absolute left-1/2 top-0 w-px h-full ${getRainColor()} animate-matrix-rain`} style={{animationDelay: '0.5s'}}></div>
        <div className={`matrix-rain absolute left-3/4 top-0 w-px h-full ${getRainColor()} animate-matrix-rain`} style={{animationDelay: '1s'}}></div>
      </div>
      
      {/* Enhanced Floating Tech Icons with Particles */}
      <div className="absolute top-20 left-4 sm:left-10 opacity-30 hidden sm:block">
        <motion.div
          animate={{ y: [0, -20, 0], rotate: [0, 5, 0] }}
          transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
          className="text-2xl sm:text-4xl text-teal-400"
        >
          ⚡
        </motion.div>
        {/* Particle effect around lightning */}
        <div className="absolute inset-0">
          {[...Array(3)].map((_, i) => (
            <motion.div
              key={i}
              animate={{ 
                x: [0, Math.random() * 20 - 10], 
                y: [0, Math.random() * 20 - 10],
                opacity: [0, 1, 0]
              }}
              transition={{ 
                duration: 2 + i, 
                repeat: Infinity, 
                delay: i * 0.5,
                ease: "easeInOut"
              }}
              className="absolute w-1 h-1 bg-teal-400 rounded-full"
              style={{
                left: Math.random() * 20,
                top: Math.random() * 20
              }}
            />
          ))}
        </div>
      </div>
      
      <div className="absolute top-40 right-4 sm:right-20 opacity-30 hidden sm:block">
        <motion.div
          animate={{ y: [0, 15, 0], rotate: [0, -3, 0] }}
          transition={{ duration: 3, repeat: Infinity, ease: "easeInOut" }}
          className="text-2xl sm:text-3xl text-teal-400"
        >
          🚀
        </motion.div>
        {/* Rocket trail effect */}
        <div className="absolute -bottom-4 left-1/2 transform -translate-x-1/2">
          <motion.div
            animate={{ 
              scaleY: [1, 1.5, 1],
              opacity: [0.5, 0.8, 0.5]
            }}
            transition={{ 
              duration: 1.5, 
              repeat: Infinity,
              ease: "easeInOut"
            }}
            className="w-1 h-8 bg-gradient-to-t from-teal-400 to-transparent"
          />
        </div>
      </div>
      
      <div className="absolute bottom-40 left-4 sm:left-20 opacity-30 hidden sm:block">
        <motion.div
          animate={{ y: [0, -10, 0], rotate: [0, 2, 0] }}
          transition={{ duration: 5, repeat: Infinity, ease: "easeInOut" }}
          className="text-xl sm:text-2xl text-neon-600"
        >
          💻
        </motion.div>
        {/* Code lines animation - fixed positioning */}
        <div className="absolute right-2 top-2 overflow-hidden">
          {[...Array(3)].map((_, i) => (
            <motion.div
              key={i}
              animate={{ 
                x: [0, 10, 0],
                opacity: [0, 1, 0]
              }}
              transition={{ 
                duration: 2, 
                repeat: Infinity, 
                delay: i * 0.3,
                ease: "easeInOut"
              }}
              className="w-12 h-0.5 bg-neon-600 mb-1"
            />
          ))}
        </div>
      </div>

      {/* Additional floating elements - hidden on mobile */}
      <div className="absolute top-60 right-40 opacity-20 hidden lg:block">
        <motion.div
          animate={{ 
            y: [0, -15, 0], 
            rotate: [0, 360, 0],
            scale: [1, 1.1, 1]
          }}
          transition={{ duration: 6, repeat: Infinity, ease: "easeInOut" }}
          className="text-xl text-sky-400"
        >
          🔧
        </motion.div>
      </div>

      <div className="absolute bottom-60 right-10 opacity-20 hidden lg:block">
        <motion.div
          animate={{ 
            y: [0, 20, 0], 
            rotate: [0, -180, 0]
          }}
          transition={{ duration: 8, repeat: Infinity, ease: "easeInOut" }}
          className="text-lg text-green-400"
        >
          ⚙️
        </motion.div>
      </div>
      
      <div className="container mx-auto px-2 sm:px-4 md:px-6 py-12 sm:py-16 md:py-16 relative z-10">
        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate="visible"
          className="flex flex-col lg:flex-row items-center gap-6 sm:gap-8"
        >
          {/* Profile Photo Section */}
          <motion.div variants={itemVariants} className="flex-shrink-0">
            <div className="profile-frame w-48 h-48 sm:w-56 sm:h-56 md:w-64 md:h-64 relative">
              <img 
                src={profilePhoto} 
                alt="Pradap Pandiyan" 
                className="w-full h-full object-contain"
                loading="eager"
                decoding="async"
              />
              {/* Matrix-style overlay */}
              <div className="absolute inset-0 bg-gradient-to-br from-teal-400/20 to-transparent"></div>
              <div className="absolute top-4 right-4 w-4 h-4 bg-teal-400 rounded-full animate-terminal-blink"></div>
              {/* Terminal-style corner indicators */}
              <div className="absolute top-2 left-2 w-2 h-2 bg-teal-400 rounded-full"></div>
              <div className="absolute bottom-2 right-2 w-2 h-2 bg-teal-400 rounded-full"></div>
            </div>
          </motion.div>

          {/* Content Section */}
          <div className="flex-1 space-y-4 sm:space-y-6 text-center lg:text-left">
            <motion.div variants={itemVariants} className="space-y-4">
              <h1 className={`text-3xl sm:text-4xl lg:text-6xl font-bold leading-tight ${themeClasses.text}`}>
                Hi, I'm <span className={`bg-gradient-to-r ${themeClasses.gradient} bg-clip-text text-transparent`}>Pradap Pandiyan</span>
              </h1>
              <h2 className={`text-lg sm:text-xl lg:text-3xl ${themeClasses.text} font-medium`}>
                Platform Automation Lead & Software Developer
              </h2>
            </motion.div>

            <motion.p
              variants={itemVariants}
              className={`text-base sm:text-lg ${themeClasses.text} leading-relaxed max-w-2xl mx-auto lg:mx-0`}
            >
              Passionate and results-driven QA Automation Engineer with expertise in testing automation, 
              mobile app development, and quality assurance. Based in Dubai, UAE, with extensive experience 
              at leading companies like M2 and Exinity.
            </motion.p>

            <motion.div
              variants={itemVariants}
              className="flex flex-wrap gap-3 sm:gap-4 justify-center lg:justify-start"
            >
              <MagneticButton
                onClick={goToContact}
                strength={0.4}
                className={`${themeClasses.bg} border-2 ${
                  theme === 'slate' ? 'border-teal-400 text-teal-400 hover:bg-teal-400 hover:text-slate-950' :
                  theme === 'midnight' ? 'border-sky-400 text-sky-400 hover:bg-sky-400 hover:text-white' :
                  theme === 'studio' ? 'border-teal-700 text-teal-700 hover:bg-teal-700 hover:text-white' :
                  'border-sky-400 text-sky-400 hover:bg-sky-400 hover:text-white'
                } text-sm sm:text-base px-4 py-2 sm:px-6 sm:py-3 rounded-lg font-semibold transition-all duration-300 shadow-lg hover:shadow-xl`}
              >
                Get In Touch
              </MagneticButton>
              <MagneticButton
                onClick={goToProducts}
                strength={0.4}
                className={`border-2 ${
                  theme === 'slate' ? 'border-teal-400 text-teal-400 hover:bg-teal-400 hover:text-slate-950' :
                  theme === 'midnight' ? 'border-sky-400 text-sky-400 hover:bg-sky-400 hover:text-white' :
                  theme === 'studio' ? 'border-teal-700 text-teal-700 hover:bg-teal-700 hover:text-white' :
                  'border-teal-400 text-teal-400 hover:bg-teal-400 hover:text-white'
                } ${themeClasses.bg} text-sm sm:text-base px-4 py-2 sm:px-6 sm:py-3 rounded-lg font-semibold transition-all duration-300 shadow-lg hover:shadow-xl`}
              >
                View Products
              </MagneticButton>
            </motion.div>

            <motion.div
              variants={itemVariants}
              className="flex flex-col sm:flex-row items-center space-y-4 sm:space-y-0 sm:space-x-6"
            >
              <div className="flex space-x-4">
                <motion.a
                  whileHover={{ scale: 1.1, y: -2 }}
                  href={contactInfo.linkedin}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="LinkedIn"
                  className={`p-3 rounded-full ${themeClasses.bg} border-2 ${
                    theme === 'slate' ? 'border-teal-400 text-teal-400 hover:bg-teal-400 hover:text-slate-950' :
                    theme === 'midnight' ? 'border-sky-400 text-sky-400 hover:bg-sky-400 hover:text-white' :
                    theme === 'studio' ? 'border-teal-700 text-teal-700 hover:bg-teal-700 hover:text-white' :
                    'border-sky-400 text-sky-400 hover:bg-sky-400 hover:text-white'
                  } transition-colors duration-300`}
                >
                  <FaLinkedin className="w-5 h-5" />
                </motion.a>
                <motion.a
                  whileHover={{ scale: 1.1, y: -2 }}
                  href="https://github.com/pradapjackie"
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="GitHub"
                  className={`p-3 rounded-full ${themeClasses.bg} border-2 ${
                    theme === 'slate' ? 'border-teal-400 text-teal-400 hover:bg-teal-400 hover:text-slate-950' :
                    theme === 'midnight' ? 'border-sky-400 text-sky-400 hover:bg-sky-400 hover:text-white' :
                    theme === 'studio' ? 'border-teal-700 text-teal-700 hover:bg-teal-700 hover:text-white' :
                    'border-teal-400 text-teal-400 hover:bg-teal-400 hover:text-white'
                  } transition-colors duration-300`}
                >
                  <FaGithub className="w-5 h-5" />
                </motion.a>
                <motion.a
                  whileHover={{ scale: 1.1, y: -2 }}
                  href={`mailto:${contactInfo.email}`}
                  aria-label="Email"
                  className={`p-3 rounded-full ${themeClasses.bg} border-2 ${
                    theme === 'slate' ? 'border-neon-600 text-neon-600 hover:bg-neon-600 hover:text-slate-950' :
                    theme === 'midnight' ? 'border-pink-400 text-pink-400 hover:bg-pink-400 hover:text-white' :
                    theme === 'studio' ? 'border-blue-600 text-blue-600 hover:bg-blue-600 hover:text-white' :
                    'border-blue-300 text-blue-300 hover:bg-blue-300 hover:text-white'
                  } transition-colors duration-300`}
                >
                  <FaEnvelope className="w-5 h-5" />
                </motion.a>
                <motion.a
                  whileHover={{ scale: 1.1, y: -2 }}
                  href="https://www.youtube.com/@TamilCarDudes"
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Tamil Car Dudes on YouTube"
                  title="Tamil Car Dudes"
                  className={`p-3 rounded-full ${themeClasses.bg} border-2 border-red-500 text-red-500 hover:bg-red-500 hover:text-white transition-colors duration-300`}
                >
                  <FaYoutube className="w-5 h-5" />
                </motion.a>
                <motion.a
                  whileHover={{ scale: 1.1, y: -2 }}
                  href="https://www.youtube.com/@themototriangle"
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Moto Triangle on YouTube"
                  title="Moto Triangle"
                  className={`p-3 rounded-full ${themeClasses.bg} border-2 border-orange-500 text-orange-500 hover:bg-orange-500 hover:text-white transition-colors duration-300`}
                >
                  <FaYoutube className="w-5 h-5" />
                </motion.a>
              </div>
              <div className={`flex items-center ${themeClasses.text}`}>
                <FaMapMarkerAlt className="w-4 h-4 mr-2" />
                <span>{contactInfo.location}</span>
              </div>
            </motion.div>
          </div>

          <motion.div
            variants={itemVariants}
            className="flex justify-center lg:justify-end mt-8 lg:mt-0 w-full max-w-md mx-auto lg:mx-0"
          >
            <FloatingProductIcons />
          </motion.div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1 }}
          className="flex justify-center mt-16"
        >
          <motion.div
            animate={{ y: [0, 10, 0] }}
            transition={{ duration: 2, repeat: Infinity }}
            className={`w-6 h-10 border-2 ${themeClasses.border}/50 rounded-full flex justify-center`}
          >
            <motion.div
              animate={{ y: [0, 12, 0] }}
              transition={{ duration: 2, repeat: Infinity }}
              className={`w-1 h-3 ${
                theme === 'slate' ? 'bg-teal-400' :
                theme === 'midnight' ? 'bg-sky-400' :
                theme === 'studio' ? 'bg-teal-700' :'bg-sky-400'
              } rounded-full mt-2`}
            />
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};

export default Hero;
